import { Button, Grid, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { forgotPassword, confirmPasswordCode } from '@webapps/business';
import { useMutation, useToast } from '@webapps/reactjs-tech-components';
import { FormattedMessage } from 'react-intl';
import { useStyles, TextField, combine, linkpickTheme } from '@webapps/styles';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import theme from '../theme';

const cognitoErrorForgotPassword = (code) => {
  switch (code) {
    case 'ExpiredCodeException':
      return 'Votre code de confirmation a expiré';
    case 'TooManyRequestsException':
      return 'Veuillez vous connecter ultérieurement';
    case 'LimitExceededException':
      return 'Vous avez effectué trop de tentatives, veuillez réessayer plus tard';
    case 'UserNotConfirmedException':
      return 'Merci de valider votre e-mail';
    default:
      return 'Veuillez contacter le service informatique support-technique@linkpick.fr';
  }
};


const ForgotPassword = ({ step, onNext, onClose, setForgotPasswordSubmit, email }) => {

  if (step === 1) {
    return <StepOne next={onNext} setForgotPasswordSubmit={setForgotPasswordSubmit} />;
  } else if (step === 2) {
    return <StepTwo email={email} onClose={onClose} setForgotPasswordSubmit={setForgotPasswordSubmit} />;
  }

  return null;
};

ForgotPassword.propTypes = {
  step: PropTypes.number,
  onNext: PropTypes.func,
  onClose: PropTypes.func,
  setForgotPasswordSubmit: PropTypes.func,
  email: PropTypes.string,
};

const signInSchema = yup.object().shape({
  username: yup.string().email('E-mail Invalide').required('Requis'),
});

const StepOne = ({ next, setForgotPasswordSubmit }) => {

  const {
    css,
    theme: { spacing },
    classes,
  } = useStyles(styles);

  const { addToast } = useToast();

  const {
    error: errorForgotPWD,
    isLoading: isLoadingPWD,
    isSuccess: isSuccessPWD,
    mutateAsync: mutateForgotPassword,
  } = useMutation(forgotPassword);

  const formik = useFormik({
    initialValues: {
      username: '',
    },
    validationSchema: signInSchema,
    onSubmit: async ({ username }) => {
      console.log("🚀 ~ onSubmit: ~ username:", username)

      await mutateForgotPassword({
        username,
      });
    },
  });

  // Exposer la fonction handleSubmit au parent pour StepOne
  if (setForgotPasswordSubmit) {
    setForgotPasswordSubmit(() => formik.handleSubmit);
  }

  useEffect(() => {
    if (isSuccessPWD) {
      addToast({
        status: 'success',
        message: 'Vous avez reçu un code de vérification par e-mail',
      });
      next(formik.values.username);
    }
    if (errorForgotPWD) {
      addToast({
        status: 'error',
        message: cognitoErrorForgotPassword(errorForgotPWD?.code),
      });
    }
  }, [isSuccessPWD, errorForgotPWD]);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // margin: spacing(3),
        maxWidth: '100%',
        overflow: 'hidden',
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <div className={classes["framerDivLabel"]}>
        <div className={classes["framerP3"]}>Adresse email</div>
        <TextField
          name="username"
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.username && Boolean(formik.errors.username)}
          style={{ width: '100%', maxWidth: '100%' }}
        />
        {formik.touched.username && formik.errors.username ? (
          <div className={classes["framerError"]}>{formik.errors.username}</div>
        ) : null}
      </div>
    </div>
  );
};

StepOne.propTypes = {
  next: PropTypes.func,
  setForgotPasswordSubmit: PropTypes.func,
};

const signUpSchema = yup.object({
  username: yup.string().email('E-mail Invalide'),
  otp: yup.string().required('Requis'),
  newpassword: yup
    .string()
    .required('Requis')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z\d\s\W]{8,128}$/,
      'Le mot de passe doit contenir au minimum 8 caractères, une majuscule, une minuscule et un chiffre .'
    ),
  repeatNewPassword: yup
    .string()
    .required('Requis')
    .test('passwords-match', 'Les mots de passe ne correspondent pas', function (value) {
      return this.parent.newpassword === value;
    }),
});

const StepTwo = ({ email, onClose, setForgotPasswordSubmit }) => {
  const {
    classes,
    css,
    theme: { spacing },
  } = useStyles(styles);

  const { addToast } = useToast();
  const [error, setError] = useState(false);

  const {
    error: errorForgotPWD,
    isLoading: isLoadingPWD,
    isSuccess: isSuccessPWD,
    mutateAsync: mutateForgotPassword,
  } = useMutation(confirmPasswordCode);

  const formik = useFormik({
    initialValues: {
      username: email,
      otp: '',
      newpassword: '',
      repeatNewPassword: '',
    },
    validationSchema: signUpSchema,
    enableReinitialize: true,
    onSubmit: async ({ newpassword, otp, repeatNewPassword, username }) => {
      alert("test");
      setError(false);
      otp = otp.replace(/\s+/g, '');
      if (newpassword === repeatNewPassword) {
        await mutateForgotPassword({
          username,
          otp,
          newpassword,
        });
      } else {
        setError(true);
      }
    },
  });

  // Exposer la fonction handleSubmit au parent pour StepTwo
  if (setForgotPasswordSubmit) {
    setForgotPasswordSubmit(() => formik.handleSubmit);
  }

  useEffect(() => {
    if (isSuccessPWD) {
      addToast({
        status: 'success',
        message: 'Votre mot de passe a bien été modifié',
      });
      onClose();
    }
  }, [isSuccessPWD, errorForgotPWD]);

  const [showPassword1, setShowPassword1] = useState(false);
  const handleClickShowPassword1 = () => setShowPassword1(!showPassword1);
  const handleMouseDownPassword1 = () => setShowPassword1(!showPassword1);

  const [showPassword2, setShowPassword2] = useState(false);
  const handleClickShowPassword2 = () => setShowPassword2(!showPassword2);
  const handleMouseDownPassword2 = () => setShowPassword2(!showPassword2);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: spacing(3),
        maxWidth: '100%',
        overflow: 'hidden',
        '@media (max-width:768px)': { margin: spacing(1) }
      })}
    >
      <Grid
        container
        spacing={2}
        style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}
      >
        {error && (
          <div className={classes["framerError"]} style={{ textAlign: 'center', marginBottom: spacing(1.5) }}>
            Les mots de passe doivent être identique
          </div>
        )}
        {errorForgotPWD && (
          <div className={classes["framerError"]} style={{ textAlign: 'center', marginBottom: spacing(1.5) }}>
            {cognitoErrorForgotPassword(errorForgotPWD?.code)}
          </div>
        )}
        <Grid item xs={12} style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <div className={classes["framerDivLabel"]}>
            <label className={classes["framerP3"]} htmlFor="otp">Code de vérification</label>

            <TextField
              name="otp"
              value={formik.values.otp}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.otp && Boolean(formik.errors.otp)}
              style={{ width: '100%', maxWidth: '100%' }}
            />
          </div>
          {formik.touched.otp && formik.errors.otp ? (
            <div className={classes["framerError"]}>{formik.errors.otp}</div>
          ) : null}
        </Grid>
        <Grid item xs={12} style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <div className={classes["framerDivLabel"]}>
            <label className={classes["framerP3"]} htmlFor="username"><FormattedMessage id="signIn.mail" /></label>
            <TextField
              name="username"
              disabled
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{ width: '100%', maxWidth: '100%' }}
            />
          </div>
        </Grid>
        <Grid item xs={12} style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <div className={classes["framerDivLabel"]}>
            <label className={classes["framerP3"]} htmlFor="newpassword">
              <FormattedMessage id="student.settings.subMenu.security.item.password.newPassword" />
            </label>

            <div className={css({ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: '100%' })}>
              <TextField
                name="newpassword"
                type={showPassword1 ? 'text' : 'password'}
                value={formik.values.newpassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.newpassword && Boolean(formik.errors.newpassword)}
                style={{ width: '100%', maxWidth: '100%' }}
              />
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword1}
                onMouseDown={handleMouseDownPassword1}
                className={css({ position: 'absolute', right: 8, zIndex: 1 })}
              >
                {showPassword1 ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </div>
          </div>
          {formik.touched.newpassword && formik.errors.newpassword ? (
            <div className={classes["framerError"]}>{formik.errors.newpassword}</div>
          ) : null}
        </Grid>
        <Grid item xs={12} style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <div className={classes["framerDivLabel"]}>
            <label className={classes["framerP3"]} htmlFor="repeatNewPassword">
              <FormattedMessage id="student.settings.subMenu.security.item.password.repeatNewPassword" />
            </label>

            <div className={css({ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: '100%' })}>
              <TextField
                name="repeatNewPassword"
                type={showPassword2 ? 'text' : 'password'}
                value={formik.values.repeatNewPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.repeatNewPassword && Boolean(formik.errors.repeatNewPassword)}
                style={{ width: '100%', maxWidth: '100%' }}
              />
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword2}
                onMouseDown={handleMouseDownPassword2}
                className={css({ position: 'absolute', right: 8, zIndex: 1 })}
              >
                {showPassword2 ? (
                  <Visibility fontSize="small" />
                ) : (
                  <VisibilityOff fontSize="small" />
                )}
              </IconButton>
            </div>
          </div>
          {formik.touched.repeatNewPassword && formik.errors.repeatNewPassword ? (
            <div className={classes["framerError"]}>{formik.errors.repeatNewPassword}</div>
          ) : null}
        </Grid>
      </Grid>
      {/* <Grid container spacing={2}>
        <Grid
          className={css({
            textAlign: 'center',
            marginTop: spacing(4),
          })}
          item
          xs
        >
          <Button
            className={css({ borderRadius: 400 })}
            color="primary"
            fullWidth
            disabled={isLoadingPWD}
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
          >
            Confirmer
          </Button>
        </Grid>
      </Grid> */}
    </div>
  );
};

StepTwo.propTypes = {
  email: PropTypes.string,
  onClose: PropTypes.func,
  setForgotPasswordSubmit: PropTypes.func,
};

const styles = () => {
  const { fontSizes2, colors, gap, padding, iconSizes } = linkpickTheme.params;

  return {
    "framerP3": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Roboto\", sans-serif",
      color: colors.blancGlacier,
    },
    "framerDivLabel": {
      ...combine(gap.xs),
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
    },
    "framerError": {
      ...combine(fontSizes2.body),
      fontFamily: "\"Roboto\", sans-serif",
      color: colors.roseElectrique,
      wordWrap: "break-word",
      whiteSpace: "normal",
      maxWidth: "100%",
      overflow: "hidden",
    },
  }
};

export default ForgotPassword;